import { HealthGoalTypesType } from 'components/molecules/HealthGoalSelection/HealthGoalSelection';
import { UNDERWEIGHT_THRESHOLD } from 'defs/bmi';
import { ProgramTargets } from 'defs/program';
import { ProgramPreviewStateType } from 'store/programPreview';
import { IRootState } from 'store/rootReducer';
import { ImageProps } from '../../components/atoms/Image/Image';
import { GenderKeyType } from '../../types/GenderKey';
import { getUserData, ProfileDataType } from '../profile';
import { ProgramOverviewItemType, ProgramOverviewUserDataType } from './programOverview';

export const isRecommendedProgram = (
	program: ProgramPreviewStateType | ProgramOverviewItemType,
	intensity: number | null,
	healthGoal: HealthGoalTypesType | null
) => {
	// intensity === 0 means it's a personalized program PULSWM-3721
	if (program.intensity === intensity || program.intensity === 0) {
		return program.goal === healthGoal;
	}
	return false;
};

export const getExternalIdList = (state: IRootState): Array<string> => {
	return state.programOverview.externalIdList;
};

export const getHasError = (state: IRootState): boolean => {
	return !!state.programOverview.error;
};

export const getProgramByExternalId = (state: IRootState, externalId: string): ProgramOverviewItemType | null => {
	if (state.programOverview.programByExternalId) {
		return state.programOverview.programByExternalId[externalId];
	}

	return null;
};

export const getLoadingState = (state: IRootState): boolean => {
	return !!state.programOverview.loading;
};

function stripIllegalPrograms<T extends any = any>(
	userData: ProgramOverviewUserDataType,
	programList: Array<T>
): Array<T> {
	const { pregnant, bmi, healthGoal } = userData;

	// healthierLifestyle = no filtering => PULSWM-3977
	if (bmi === null || typeof pregnant !== 'boolean' || healthGoal === 'HEALTHY_LIFESTYLE') {
		return programList;
	}

	const stripped = programList.map((program: T | any): T | null => {
		if (!program) {
			return null;
		}

		if (program.goal === ProgramTargets.LoseWeight && (pregnant || bmi <= UNDERWEIGHT_THRESHOLD)) {
			return null;
		}

		return program;
	});

	return stripped.filter(Boolean) as any;
}

export const filterByProgramBlacklist = (
	userData: ProfileDataType,
	programList: DecoratedProgramOverviewItemType[]
): DecoratedProgramOverviewItemType[] => {
	if (programList.length === 0) {
		return [];
	}

	const { nutritionPhilosophy, allergies } = userData;
	const userValues = [nutritionPhilosophy, ...allergies];

	const valueMap = {
		'root.vegan.': 'VEGAN',
		'root.meat-fish.': 'MEAT_FISH',
		'root.vegetarian.': 'VEGETARIAN',
		'root.gluten.': 'GLUTEN',
		'root.lactose.': 'LACTOSE',
	} as const;

	return programList.filter((program) => {
		const blacklistMappedValues = program.blacklist.map((blacklist) => valueMap[blacklist.value]);

		return !blacklistMappedValues.some((blacklistMappedValue) => {
			// vegi blacklisted needed also to be vegan blacklisted
			if (blacklistMappedValue === 'VEGETARIAN') {
				return userValues.includes('VEGETARIAN') || userValues.includes('VEGAN');
			}
			return userValues.includes(blacklistMappedValue);
		});
	});
};

export const getRequiredUserData = (state: IRootState): ProgramOverviewUserDataType => {
	return state.programOverview.requiredUserData;
};

export type DecoratedProgramOverviewItemType = ProgramOverviewItemType & { reactShowFlag: boolean };

export const getPreviewImageByGender = (program: Partial<ProgramOverviewItemType>, gender: GenderKeyType | null) => {
	let previewImage = program.previewImage;
	if (gender === 'M' && program.previewImageMale) {
		previewImage = program.previewImageMale;
	} else if (gender === 'F' && program.previewImageFemale) {
		previewImage = program.previewImageFemale;
	}
	return previewImage;
};

export const getRecommendedPrograms = (
	state: IRootState,
	userIntensity: number | null,
	healthGoal: HealthGoalTypesType | null,
	gender: GenderKeyType | null
): DecoratedProgramOverviewItemType[] => {
	const ids: Array<string> = getExternalIdList(state);
	const requiredUserData = getRequiredUserData(state);
	const userData = getUserData(state);
	const recommended: any[] = [];

	ids.forEach((extId: string) => {
		const program = getProgramByExternalId(state, extId);
		if (program) {
			const previewImage = getPreviewImageByGender(program, gender);
			if (isRecommendedProgram(program, userIntensity, healthGoal)) {
				recommended.push({
					...program,
					previewImage,
					reactShowFlag: true,
					intensity: (program.intensity === 0 ? userIntensity : program.intensity) || 0,
				});
			}
		}
	});

	if (!userData) {
		return stripIllegalPrograms(requiredUserData, recommended);
	}

	return filterByProgramBlacklist(userData, stripIllegalPrograms(requiredUserData, recommended));
};

export const getNotRecommendedPrograms = (
	state: IRootState,
	userIntensity: number | null,
	healthGoal: HealthGoalTypesType | null,
	gender: GenderKeyType | null
): DecoratedProgramOverviewItemType[] | null => {
	const ids = getExternalIdList(state);
	const requiredUserData = getRequiredUserData(state);
	const userData = getUserData(state as any);
	const notRecommended = ids
		.map((extId: string): DecoratedProgramOverviewItemType | null => {
			const program = getProgramByExternalId(state, extId);

			if (program) {
				const previewImage = getPreviewImageByGender(program, gender) as ImageProps;
				if (!isRecommendedProgram(program, userIntensity, healthGoal)) {
					return {
						...program,
						previewImage,
						reactShowFlag: false,
						intensity: (program.intensity === 0 ? userIntensity : program.intensity) || 0,
					};
				}
			}

			return null;
		})
		.filter(Boolean);

	return filterByProgramBlacklist(userData as any, stripIllegalPrograms(requiredUserData, notRecommended) as any);
};

export const getAllPrograms = (state: IRootState, intensity: number, healthGoal: HealthGoalTypesType) => {
	const ids = getExternalIdList(state);

	return ids.map((extId: string) => {
		const program = getProgramByExternalId(state, extId);
		let reactShowFlag: boolean = false;

		if (program) {
			reactShowFlag = isRecommendedProgram(program, intensity, healthGoal);
		}

		return {
			...program,
			reactShowFlag,
		} as DecoratedProgramOverviewItemType;
	});
};
